import Button from "@components/common/Button";
import CustomAccordion from "@components/common/CustomAccordion";
import DownloadIcon from "@components/icons/DownloadIcon";
import { useIsMobile } from "@components/mediaQuery/useIsMobile";
import { fetchCyberSecurity } from "@services/vesselService";
import { loadingCybersecurity, setCybersecurityData } from "@store/modules/vesselsSlice";
import { useEffect } from "react";
import Chart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

const Cybersecurity = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const token = useSelector((state) => state.auth.user.token);

	const cybersecurityData = useSelector((state) => state.modules.vessels.cybersecurity?.data) || [];

	const reports = Array.isArray(cybersecurityData) ? cybersecurityData : [];
	const loading = useSelector((state) => state.modules.vessels?.cybersecurity?.loading);

	const isMobile = useIsMobile();

	useEffect(() => {
		if (!id || !token) return;

		const fetchData = async () => {
			dispatch(loadingCybersecurity(true));
			const data = await fetchCyberSecurity(token, id, dispatch, navigate);
			dispatch(setCybersecurityData(data || []));
		};

		fetchData();
	}, [id, token, dispatch, navigate]);

	if (loading) {
		return (
			<div className="flex h-64 items-center justify-center">
				<div className="h-32 w-32 animate-spin rounded-full border-gray-700 border-t-2 border-b-2"></div>
			</div>
		);
	}
	const pdfFile = (Array.isArray(cybersecurityData) ? cybersecurityData : []).find(
		(report) => report.pdf_file,
	)?.pdf_file;

	const handleDownload = () => {
		if (pdfFile) {
			const link = document.createElement("a");
			link.href = pdfFile;
			link.download = pdfFile.split("/").pop();
			link.click();
		} else {
			console.error("No report PDF available.");
		}
	};

	return (
		<div>
			<CustomAccordion
				title="REPORTS"
				id="info"
				isOpen={true}
				classCss="border-0 bg-[#1F2A37]"
				showButton={true}
				isMobile={isMobile}
				customButton={
					<div className="flex gap-2">
						{[
							{
								label: "Network Usage",
								index: 0,
								fileKey: "traffic_file",
							},
							{
								label: "EndPoint Report",
								index: 1,
								fileKey: "forticlient_file",
							},
							{
								label: "SOC Report",
								index: 2,
								fileKey: "soc_file",
							},
							{
								label: "Corporate Report",
								index: 3,
								fileKey: "corporate_file",
							},
							{
								label: "Welfare Report",
								index: 4,
								fileKey: "welfare_file",
							},
						].map(({ label, index, fileKey }) => {
							const fileUrl = cybersecurityData?.[index]?.[fileKey];
							const fullUrl = fileUrl ? `${import.meta.env.VITE_API_BACKEND}${fileUrl}` : null;

							return (
								<Button
									key={fileKey}
									text={label}
									className="!p-2 !m-0 text-sm text-white"
									color="blue"
									icon={DownloadIcon}
									position="after"
									classNameIcon="w-4 h-4 ms-2 text-white"
									disabled={!fileUrl}
									onClick={() => window.open(fullUrl, "_blank")}
								/>
							);
						})}
					</div>
				}
			>
				{reports.length > 0 ? (
					<>
						{reports[3].date && (
							<p className="truncate text-start font-light text-blue-gray text-sm">
								<span className="font-semibold">{reports[3]?.title} :</span> {reports[3]?.date}
							</p>
						)}

						{/* the rest of code */}
						{reports
							.filter((report) => report.title)
							.map((report, reportIndex) => {
								return (
									<div key={reportIndex} className="mb-6 text-gray-300">
										{(report?.charts?.values?.length > 0 ||
											report?.tableValues?.length > 0 ||
											report?.macro?.length > 0) && (
											<>
												<h3 className="flex flex-start font-bold text-lg">{report.title}</h3>

												{report.subtitle && (
													<h2 className="flex flex-start font-bold text-lg">{report.subtitle}</h2>
												)}
											</>
										)}

										{/* Affichage des macros s'il y en a */}
										{report.macro && report.macro.length > 0 && (
											<div className="mt-4 mb-4 grid grid-cols-4 gap-4 text-gray-400">
												{report.macro.map((item, idx) => (
													<div key={idx} className="flex flex-col items-center p-4 text-gray-300">
														<p className="font-semibold">{item.name}</p>
														<p>{item.value}</p>
													</div>
												))}
											</div>
										)}

										{/* Donut and Pie Charts Displayed Side by Side */}
										<div className="flex flex-wrap gap-4">
											{report.charts && (
												<>
													{report.charts.type === "donut" && report.charts?.values?.length > 0 && (
														<div className="flex w-full justify-center">
															<div className="mb-4 w-full max-w-[500px] text-gray-300">
																<Chart
																	options={{
																		chart: { type: "donut" },
																		labels: report.charts.labels,
																		colors: report.charts.colors.map((color) =>
																			color.replace(/fill:\s?|;/g, "").trim(),
																		),
																		legend: { position: "bottom" },
																		plotOptions: {
																			pie: {
																				donut: {
																					size: "65%",
																					labels: { show: true },
																				},
																			},
																		},
																		stroke: {
																			show: true,
																			width: 2,
																			colors: ["#374151"],
																		},
																	}}
																	series={report.charts.values.map(
																		(label) => parseFloat(label) || 0,
																	)}
																	type="donut"
																	width="100%"
																/>
															</div>
														</div>
													)}

													{report.charts.type === "pie" && (
														<div className="flex w-full justify-center">
															<div className="mb-4 w-full max-w-[500px] text-gray-300">
																<Chart
																	options={{
																		chart: { type: "pie" },
																		labels: report.charts.labels,
																		colors: report.charts.colors,
																		legend: { position: "bottom" },
																		plotOptions: {
																			pie: {
																				donut: {
																					size: "65%",
																					labels: { show: true },
																				},
																			},
																		},
																		stroke: {
																			show: true,
																			width: 2,
																			colors: ["#374151"],
																		},
																	}}
																	series={report.charts.values}
																	type="pie"
																	width="400"
																/>
															</div>
														</div>
													)}
												</>
											)}
										</div>

										{/* Table Values */}
										{report.tableValues && report.tableValues.length > 0 && (
											<div className="relative mt-4 overflow-x-auto">
												<table className="w-full text-left text-gray-500 text-sm rtl:text-right dark:text-gray-400">
													<thead className="bg-[#727DA333] text-gray-400 text-xs uppercase">
														<tr>
															{Object.keys(report.tableValues[0]).map((key, index) => (
																<th key={index} scope="col" className="px-6 py-3">
																	{key
																		.replace(/([A-Z])/g, " $1")
																		.replace(/^./, (str) => str.toUpperCase())}
																</th>
															))}
														</tr>
													</thead>
													<tbody>
														{report.tableValues.map((item, index) => (
															<tr key={index} className="border-gray-700 border-b">
																{Object.keys(item).map((key, idx) => (
																	<td key={idx} className="px-6 py-4">
																		{key.includes("Icon") || key === "Risk" ? (
																			<img
																				src={`${import.meta.env.VITE_API_BACKEND}/${item[key]}`}
																				alt={`${key} Icon`}
																				className="h-8 w-8"
																			/>
																		) : (
																			item[key]
																		)}
																	</td>
																))}
															</tr>
														))}
													</tbody>
												</table>
											</div>
										)}
									</div>
								);
							})}
					</>
				) : (
					<div className="text-center text-white">No reports available</div>
				)}
			</CustomAccordion>
		</div>
	);
};

export default Cybersecurity;
